/**
 * BlogPostPage Component
 * Individual blog post with markdown rendering and table of contents
 */
import logger from '../../utils/logger';
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, Calendar, Share2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '../../lib/utils';
import BlogLayout from '../../layouts/blog/BlogLayout';
import { TableOfContents, BlogCard } from '../../components/blog';
import {
  getBlogBySlug,
  getAllBlogs,
  BLOG_CATEGORIES,
} from '../../content/blogs';

// Load blog content from public folder (CRA compatible)
const loadBlogContent = async (filename) => {
  try {
    const response = await fetch(`/content/blogs/es/${filename}.md`);
    if (response.ok) {
      return await response.text();
    }
  } catch (error) {
    logger.error('Error loading blog content:', error);
  }
  return null;
};

// Custom components for ReactMarkdown
const MarkdownComponents = {
  // Headings with IDs for TOC
  h2: ({ children, ...props }) => {
    const id = String(children)
      .toLowerCase()
      .replace(/[^a-z0-9áéíóúüñ\s]/g, '')
      .replace(/\s+/g, '-');
    return (
      <h2
        id={id}
        className="mb-4 mt-10 scroll-mt-24 text-2xl font-bold text-neutral-900 dark:text-white"
        {...props}
      >
        {children}
      </h2>
    );
  },
  h3: ({ children, ...props }) => {
    const id = String(children)
      .toLowerCase()
      .replace(/[^a-z0-9áéíóúüñ\s]/g, '')
      .replace(/\s+/g, '-');
    return (
      <h3
        id={id}
        className="mb-3 mt-8 scroll-mt-24 text-xl font-semibold text-neutral-900 dark:text-white"
        {...props}
      >
        {children}
      </h3>
    );
  },
  // Paragraphs
  p: ({ children, ...props }) => (
    <p
      className="mb-4 leading-relaxed text-neutral-700 dark:text-neutral-300"
      {...props}
    >
      {children}
    </p>
  ),
  // Links
  a: ({ href, children, ...props }) => (
    <a
      href={href}
      className="text-primary-600 underline decoration-primary-500/30 underline-offset-2 hover:decoration-primary-500 dark:text-primary-400"
      target={href?.startsWith('http') ? '_blank' : undefined}
      rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
      {...props}
    >
      {children}
    </a>
  ),
  // Lists
  ul: ({ children, ...props }) => (
    <ul
      className="mb-4 ml-6 list-disc space-y-2 text-neutral-700 dark:text-neutral-300"
      {...props}
    >
      {children}
    </ul>
  ),
  ol: ({ children, ...props }) => (
    <ol
      className="mb-4 ml-6 list-decimal space-y-2 text-neutral-700 dark:text-neutral-300"
      {...props}
    >
      {children}
    </ol>
  ),
  li: ({ children, ...props }) => (
    <li
      className="leading-relaxed !text-neutral-700 dark:!text-neutral-300"
      {...props}
    >
      {children}
    </li>
  ),
  // Checkboxes (GFM task lists)
  input: ({ type, checked, ...props }) => {
    if (type === 'checkbox') {
      return (
        <input
          type="checkbox"
          checked={checked}
          readOnly
          className="mr-2 h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500 dark:border-neutral-600 dark:bg-neutral-700"
          {...props}
        />
      );
    }
    return <input type={type} {...props} />;
  },
  // Blockquotes
  blockquote: ({ children, ...props }) => (
    <blockquote
      className="mb-4 border-l-4 border-primary-500 bg-primary-50 py-3 pl-4 pr-4 italic text-neutral-700 dark:border-primary-400 dark:bg-primary-950/30 dark:text-neutral-300"
      {...props}
    >
      {children}
    </blockquote>
  ),
  // Code blocks
  code: ({ inline, className, children, ...props }) => {
    if (inline) {
      return (
        <code
          className="rounded bg-neutral-100 px-1.5 py-0.5 font-mono text-sm text-neutral-800 dark:bg-neutral-800 dark:text-neutral-200"
          {...props}
        >
          {children}
        </code>
      );
    }
    return (
      <code
        className={cn(
          'block overflow-x-auto rounded-xl bg-neutral-900 p-4 font-mono text-sm text-neutral-100',
          className
        )}
        {...props}
      >
        {children}
      </code>
    );
  },
  pre: ({ children, ...props }) => (
    <pre className="mb-4 overflow-x-auto rounded-xl" {...props}>
      {children}
    </pre>
  ),
  // Tables
  table: ({ children, ...props }) => (
    <div className="mb-4 overflow-x-auto">
      <table
        className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-700"
        {...props}
      >
        {children}
      </table>
    </div>
  ),
  thead: ({ children, ...props }) => (
    <thead className="bg-neutral-100 dark:bg-neutral-800" {...props}>
      {children}
    </thead>
  ),
  th: ({ children, ...props }) => (
    <th
      className="px-4 py-3 text-left text-sm font-semibold text-neutral-900 dark:text-white"
      {...props}
    >
      {children}
    </th>
  ),
  td: ({ children, ...props }) => (
    <td
      className="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300"
      {...props}
    >
      {children}
    </td>
  ),
  tr: ({ children, ...props }) => (
    <tr
      className="border-b border-neutral-200 dark:border-neutral-700"
      {...props}
    >
      {children}
    </tr>
  ),
  // Horizontal rule
  hr: () => <hr className="my-8 border-neutral-200 dark:border-neutral-800" />,
  // Strong/Bold
  strong: ({ children, ...props }) => (
    <strong
      className="font-semibold text-neutral-900 dark:text-white"
      {...props}
    >
      {children}
    </strong>
  ),
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const { i18n, t } = useTranslation();
  const language = i18n.language || 'es';

  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);

  // Get blog metadata
  const blog = getBlogBySlug(slug);
  const category = blog ? BLOG_CATEGORIES[blog.category] : null;

  // Get related blogs (same category, excluding current)
  const relatedBlogs = getAllBlogs()
    .filter((b) => b.id !== blog?.id && b.category === blog?.category)
    .slice(0, 3);

  // Load blog content
  useEffect(() => {
    const loadContent = async () => {
      if (!blog) {
        setLoading(false);
        return;
      }

      setLoading(true);

      const fetchedContent = await loadBlogContent(blog.file);
      setContent(fetchedContent || '');
      setLoading(false);
    };

    loadContent();
  }, [blog]);

  // Handle share
  const handleShare = async () => {
    const url = window.location.href;
    const title = blog?.title[language] || blog?.title.es || '';

    if (navigator.share) {
      try {
        await navigator.share({ title, url });
      } catch (error) {
        // User cancelled or error
      }
    } else {
      // Fallback: copy to clipboard
      await navigator.clipboard.writeText(url);
      // Could show a toast here
    }
  };

  // 404 for non-existent blog
  if (!blog && !loading) {
    return (
      <BlogLayout>
        <div className="py-20 text-center">
          <h1 className="mb-4 text-4xl font-bold text-neutral-900 dark:text-white">
            {t('blog.notFound', 'Artículo no encontrado')}
          </h1>
          <p className="mb-8 text-neutral-600 dark:text-neutral-400">
            {t(
              'blog.notFoundDescription',
              'El artículo que buscás no existe o fue eliminado.'
            )}
          </p>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-6 py-3 font-medium text-white hover:bg-primary-600"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('blog.backToBlog', 'Volver al Blog')}
          </Link>
        </div>
      </BlogLayout>
    );
  }

  const title = blog?.title[language] || blog?.title.es || '';
  const categoryLabel = category?.label[language] || category?.label.es || '';

  return (
    <BlogLayout>
      {/* Back Button */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="mb-8"
      >
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-sm font-medium text-neutral-600 hover:text-primary-600 dark:text-neutral-400 dark:hover:text-primary-400"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={2.5} />
          {t('blog.backToBlog', 'Volver al Blog')}
        </Link>
      </motion.div>

      <div className="grid gap-8 lg:grid-cols-[1fr_280px]">
        {/* Main Content */}
        <motion.article
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <header className="mb-8">
            {/* Category */}
            <span className="mb-4 inline-block rounded-full bg-primary-500/10 px-3 py-1 text-sm font-medium text-primary-600 dark:bg-primary-500/20 dark:text-primary-400">
              {categoryLabel}
            </span>

            {/* Title */}
            <h1 className="mb-4 text-3xl font-bold text-neutral-900 dark:text-white sm:text-4xl">
              {title}
            </h1>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-neutral-600 dark:text-neutral-400">
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" strokeWidth={2.5} />
                {new Date(blog?.publishedAt).toLocaleDateString(language, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-4 w-4" strokeWidth={2.5} />
                {blog?.readingTime} {t('blog.minRead', 'min de lectura')}
              </span>
              <button
                onClick={handleShare}
                className="flex items-center gap-1 transition-colors hover:text-primary-600 dark:hover:text-primary-400"
              >
                <Share2 className="h-4 w-4" strokeWidth={2.5} />
                {t('blog.share', 'Compartir')}
              </button>
            </div>
          </header>

          {/* Content */}
          {loading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="h-4 animate-pulse rounded bg-neutral-200 dark:bg-neutral-800"
                  style={{ width: `${Math.random() * 40 + 60}%` }}
                />
              ))}
            </div>
          ) : (
            <div className="prose prose-neutral dark:prose-invert max-w-none">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={MarkdownComponents}
              >
                {content}
              </ReactMarkdown>
            </div>
          )}
        </motion.article>

        {/* Sidebar - Table of Contents */}
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <TableOfContents content={content} />
          </div>
        </aside>
      </div>

      {/* Related Posts */}
      {relatedBlogs.length > 0 && (
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-16 border-t border-neutral-200 pt-12 dark:border-neutral-800"
        >
          <h2 className="mb-6 text-2xl font-bold text-neutral-900 dark:text-white">
            {t('blog.relatedPosts', 'Artículos relacionados')}
          </h2>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {relatedBlogs.map((relatedBlog, index) => (
              <BlogCard key={relatedBlog.id} blog={relatedBlog} index={index} />
            ))}
          </div>
        </motion.section>
      )}
    </BlogLayout>
  );
};

export default BlogPostPage;
